"use client";

import { useRef } from "react";

export interface TiltCardProps {
  children: React.ReactNode;
  className?: string;
  /** Degrees of rotation reached at the card's far edge. */
  strength?: number;
  /** Resting tilt, rendered server-side so the depth reads without JS. */
  restX?: number;
  restY?: number;
  /** How far the card travels towards the viewer while pointed at, in px. */
  lift?: number;
  as?: "div" | "li" | "figure";
}

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** The transform for one tilt state, shared by the resting and live values. */
export function tiltTransform(
  rotateX: number,
  rotateY: number,
  lift: number
): string {
  return `perspective(1400px) rotateX(${rotateX.toFixed(2)}deg) rotateY(${rotateY.toFixed(2)}deg) translate3d(0, 0, ${lift}px)`;
}

/**
 * A surface that sits in space rather than flat on the page: it renders at a
 * resting tilt and rotates towards the pointer, so highlights and shadows
 * shift as a real object's would.
 *
 * Children can be parallaxed with `[transform:translateZ(…)]` because the card
 * preserves its 3D context. Touch pointers are ignored and reduced motion pins
 * the card to its resting angle.
 *
 * The root carries `glass-tilt`, the Liquid Glass marker for 3D hosts: glass
 * nested in it never blurs (a backdrop filter cannot run under preserve-3d),
 * so the card and its children use `glass-flat`.
 */
export default function TiltCard({
  children,
  className = "",
  strength = 6,
  restX = 0,
  restY = 0,
  lift = 24,
  as: Tag = "div",
}: TiltCardProps) {
  const ref = useRef<HTMLElement>(null);

  const settle = () => {
    const node = ref.current;
    if (node) node.style.transform = tiltTransform(restX, restY, 0);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLElement>) => {
    const node = ref.current;
    // A touch "hover" has no exit event, so a tap would leave the card
    // stuck mid-tilt. Every other pointer, including an unknown one, tilts.
    if (!node || event.pointerType === "touch" || prefersReducedMotion())
      return;

    const rect = node.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const offsetX = (event.clientX - rect.left) / rect.width - 0.5;
    const offsetY = (event.clientY - rect.top) / rect.height - 0.5;

    node.style.transform = tiltTransform(
      restX - offsetY * strength * 2,
      restY + offsetX * strength * 2,
      lift
    );
  };

  return (
    <Tag
      ref={ref as React.Ref<never>}
      data-testid="tilt-card"
      onPointerMove={handlePointerMove}
      onPointerLeave={settle}
      onBlurCapture={settle}
      style={{ transform: tiltTransform(restX, restY, 0) }}
      className={`glass-tilt [transform-style:preserve-3d] transition-transform duration-300 ease-out will-change-transform motion-reduce:transition-none ${className}`}
    >
      {children}
    </Tag>
  );
}
